import { useCallback, useEffect, useState } from 'react';

import { Alert, Badge, Button, Card, HelpTip } from '@/components/ui';
import { api } from '@/lib/api';

import { EscolherClienteDoVinculo } from './EscolherClienteDoVinculo';

/**
 * A tela de quem **NÃO** vinculou — em `Configurações › Integrações`, irmã da
 * conciliação de produtos que já vive lá.
 *
 * ⚠️ Ela é tão importante quanto o vínculo. Sem ela, quem não casa **desaparece
 * em silêncio** — e ausência silenciosa é o padrão de defeito que mais custou a
 * este projeto: o sync que ficou dois dias morto, os 211 títulos fantasmas, a
 * fila de orçamento que secaria sem ninguém notar.
 *
 * 🔴 Uma tela só, para todas as fontes. Nasce com o Auvo e serve ao Omie no
 * mesmo formato — é ali que os cadastros duplicados do #98 vão parar.
 */

type Sistema = 'auvo' | 'omie';
type Motivo = 'sem_documento' | 'documento_sem_par' | 'mais_de_um_candidato';

interface Pendente {
  sistema: Sistema;
  escopo: string;
  codigo: string;
  nome: string | null;
  documentoFormatado: string | null;
  tipoDocumento: 'cpf' | 'cnpj' | null;
  motivo: Motivo;
  motivoTitulo: string;
  oQueFazer: string;
  candidatos: Array<{ id: string; nome: string | null }>;
  semPar: boolean;
}

interface Painel {
  sistema: Sistema;
  frase: string;
  contagem: {
    vinculados: number;
    pendentes: number;
    semPar: number;
    porMotivo: Record<Motivo, number>;
    clientesDoV2SemVinculo: number;
    totalClientesDoV2: number;
  };
  pendentes: Pendente[];
  clientesDoV2SemVinculo: Array<{ id: string; razaoSocial: string | null; documentoFormatado: string | null }>;
}

const AJUDA =
  'O IGM liga um cadastro de fora a um cliente daqui pelo CNPJ ou CPF, e só por ele. ' +
  'Nunca pelo nome: nome parecido não é o mesmo cliente, e o erro que passa despercebido ' +
  'é o que acerta por sorte. Quem não tem documento fica nesta lista até alguém resolver.';

const COR: Record<Motivo, 'warning' | 'neutral' | 'danger'> = {
  sem_documento: 'warning',
  documento_sem_par: 'neutral',
  mais_de_um_candidato: 'danger',
};

export function CartaoVinculos() {
  const [sistema, setSistema] = useState<Sistema>('auvo');
  const [painel, setPainel] = useState<Painel | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [abrindo, setAbrindo] = useState<string | null>(null);
  const [verOutroLado, setVerOutroLado] = useState(false);

  const carregar = useCallback(() => {
    setErro(null);
    setCarregando(true);
    api
      .get<Painel>(`/api/v1/admin/vinculos?sistema=${sistema}`)
      .then(setPainel)
      .catch((e: Error) => setErro(e.message))
      .finally(() => setCarregando(false));
  }, [sistema]);

  useEffect(carregar, [carregar]);

  async function agir(caminho: string, corpo: Record<string, unknown>, feito: string) {
    setAviso(null);
    setErro(null);
    try {
      const r = await api.post<Painel>(`/api/v1/admin/vinculos/${caminho}`, { sistema, ...corpo });
      setPainel((p) => (p ? { ...p, ...r } : p));
      setAviso(feito);
      carregar();
    } catch (err) {
      const e = err as Error & { response?: { erro?: string } };
      setErro(e.response?.erro ?? e.message);
    }
  }

  const c = painel?.contagem;

  return (
    <Card className="p-4 sm:p-5 space-y-4" data-testid="integracao-vinculos">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-display text-xl text-ink-1 dark:text-ink-dark-1">
            Vínculos de clientes <HelpTip texto={AJUDA} />
          </h2>
          <p className="text-xs text-ink-3 dark:text-ink-dark-3">
            Quem já está ligado ao cadastro do IGM, e quem ainda não está.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(['auvo', 'omie'] as const).map((s) => (
            <Button
              key={s}
              size="sm"
              variant={sistema === s ? 'primary' : 'outline'}
              onClick={() => setSistema(s)}
              data-testid={`vinculos-aba-${s}`}
            >
              {s === 'auvo' ? 'Auvo' : 'Omie'}
            </Button>
          ))}
        </div>
      </div>

      {carregando && <p className="text-sm text-ink-3 dark:text-ink-dark-3">Consultando…</p>}
      {erro && <Alert variant="danger">{erro}</Alert>}
      {aviso && <Alert variant="info" data-testid="vinculos-aviso">{aviso}</Alert>}

      {painel && c && (
        <>
          {/* ⚠️ A CONTAGEM VEM PRIMEIRO. Uma lista sem número vira paisagem. */}
          <div className="rounded-lg bg-paper-warm p-3 dark:bg-paper-dark-warm" data-testid="vinculos-frase">
            <p className="text-sm font-semibold text-ink-1 dark:text-ink-dark-1">{painel.frase}</p>
            <p className="mt-1 text-xs text-ink-3 dark:text-ink-dark-3">
              {c.vinculados} vinculados · {c.clientesDoV2SemVinculo} dos {c.totalClientesDoV2} clientes
              do IGM ainda não têm vínculo com {sistema === 'auvo' ? 'o Auvo' : 'o Omie'}
              {c.semPar > 0 && ` · ${c.semPar} marcados como sem par`}
            </p>
          </div>

          {c.pendentes === 0 && (
            <Alert variant="success" data-testid="vinculos-tudo-certo">
              Nada esperando decisão aqui.
            </Alert>
          )}

          {painel.pendentes.filter((p) => !p.semPar).length > 0 && (
            <ul className="space-y-3" data-testid="vinculos-lista">
              {painel.pendentes
                .filter((p) => !p.semPar)
                .map((p) => {
                  const chave = `${p.sistema}:${p.escopo}:${p.codigo}`;
                  return (
                    <li
                      key={chave}
                      className="rounded-lg border border-line p-3 dark:border-line-dark"
                      data-testid="vinculo-pendente"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-semibold text-ink-1 dark:text-ink-dark-1">
                            {p.nome || '(cadastro sem nome)'}
                          </p>
                          <p className="text-xs text-ink-3 dark:text-ink-dark-3">
                            {p.documentoFormatado
                              ? `${p.tipoDocumento === 'cpf' ? 'CPF' : 'CNPJ'} ${p.documentoFormatado}`
                              : 'sem documento'}{' '}
                            · código {p.codigo}
                          </p>
                        </div>
                        <Badge variant={COR[p.motivo]} size="sm">
                          {p.motivoTitulo}
                        </Badge>
                      </div>

                      {/* 🔴 O motivo diz o que fazer. "Não vinculou" sozinho é um beco. */}
                      <p className="mt-2 text-sm text-ink-2 dark:text-ink-dark-2">{p.oQueFazer}</p>

                      {p.candidatos.length > 0 && (
                        <p className="mt-1 text-xs text-ink-3 dark:text-ink-dark-3">
                          Candidatos: {p.candidatos.map((x) => x.nome || x.id).join(' · ')}
                        </p>
                      )}

                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        {abrindo === chave ? (
                          // #571 (14/10/2026): busca por nome/CNPJ e pergunta antes de gravar; o Id interno saiu.
                          <EscolherClienteDoVinculo
                            nomeDeFora={p.nome}
                            sistema={p.sistema}
                            onCancelar={() => setAbrindo(null)}
                            onConfirmar={(clienteId) => {
                              setAbrindo(null);
                              void agir('vincular', { escopo: p.escopo, codigo: p.codigo, clienteId }, 'Vínculo criado.');
                            }}
                          />
                        ) : (
                          <>
                            {/* #627: no Omie o vínculo nasce na leitura, pelo CNPJ; à mão aqui não teria efeito. */}
                            {p.sistema === 'auvo' ? (
                              <Button size="sm" variant="outline" onClick={() => setAbrindo(chave)}>
                                Vincular à mão
                              </Button>
                            ) : (
                              <span className="text-xs text-ink-3 dark:text-ink-dark-3" data-testid="vinculo-omie-na-leitura">
                                No Omie, o vínculo nasce quando o cadastro é lido, pelo CNPJ.
                              </span>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                agir(
                                  'sem-par',
                                  { escopo: p.escopo, codigo: p.codigo, semPar: true },
                                  'Marcado como sem par. Ele sai da fila e não vira vínculo.',
                                )
                              }
                            >
                              Não tem par
                            </Button>
                          </>
                        )}
                      </div>
                    </li>
                  );
                })}
            </ul>
          )}

          {/* ⚠️ O outro lado. Pode ser normal — mas ele precisa poder ver. */}
          <div className="border-t border-line pt-3 dark:border-line-dark">
            <Button size="sm" variant="ghost" onClick={() => setVerOutroLado((v) => !v)}>
              {verOutroLado ? 'Esconder' : 'Ver'} os {c.clientesDoV2SemVinculo} clientes do IGM sem
              vínculo
            </Button>
            {verOutroLado && (
              <>
                <p className="mt-2 text-xs text-ink-3 dark:text-ink-dark-3">
                  Estar aqui costuma ser normal: nem todo cliente do IGM tem ordem de serviço no{' '}
                  {sistema === 'auvo' ? 'Auvo' : 'Omie'}.
                </p>
                <ul className="mt-2 max-h-64 space-y-1 overflow-y-auto text-sm" data-testid="vinculos-outro-lado">
                  {painel.clientesDoV2SemVinculo.map((x) => (
                    <li key={x.id} className="flex flex-wrap gap-2 text-ink-2 dark:text-ink-dark-2">
                      <span>{x.razaoSocial || '(sem razão social)'}</span>
                      <span className="text-ink-3 dark:text-ink-dark-3">
                        {x.documentoFormatado || 'sem documento'}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </>
      )}
    </Card>
  );
}
